"use client";

import React, { useState, useEffect } from "react";
import { Button } from "../../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import { Label } from "../../ui/label";
import { Input } from "../../ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";
import { Loader2, Search, MessageSquare, ArrowLeft, Table, Plus, X, FileCheck2 } from "lucide-react";
import { ReviewComment } from "../../shared/ReviewComment";
import { getFY_YEAR } from "@/utils/sharepoint-env";
import { 
  getProcedureFileUsageForWorkspace, 
  updateProcedureFileUsageForWorkspace,
  type FileUsageEntry 
} from "@/utils/fileUsageReference";

interface AdvanceFromCustomerCompletenessProps {
  onBack?: () => void;
}

interface ExcelColumn {
  name: string;
  value: string;
}

interface LedgerAccount {
  ledger_id: number;
  ledger_name: string;
  closing_balance: number;
  note_line_id: number;
}

interface UserReconciliation {
  reason: string;
  amount: number;
}

export default function AdvanceFromCustomerCompleteness({ onBack }: AdvanceFromCustomerCompletenessProps) {
  // State management
  const [excelColumns, setExcelColumns] = useState<ExcelColumn[]>([]);
  const [isLoadingExcelColumns, setIsLoadingExcelColumns] = useState(false);
  const [selectedFile, setSelectedFile] = useState<string>("");
  const [fileSearchStatus, setFileSearchStatus] = useState<"idle" | "searching" | "found" | "not_found">("idle");
  const [foundFiles, setFoundFiles] = useState<string[]>([]);
  const [reviewCommentQuestionId, setReviewCommentQuestionId] = useState<string | null>(null);
  
  // Column mappings for the 4 dropdowns
  const [partyNameMapping, setPartyNameMapping] = useState<string>("");
  const [partyCodeMapping, setPartyCodeMapping] = useState<string>("");
  const [amountMapping, setAmountMapping] = useState<string>("");
  const [dateOfReceiptMapping, setDateOfReceiptMapping] = useState<string>("");

  // Completeness Check state
  const [availableLedgerAccounts, setAvailableLedgerAccounts] = useState<LedgerAccount[]>([]);
  const [selectedLedgerAccounts, setSelectedLedgerAccounts] = useState<number[]>([]);
  const [isLoadingLedgerAccounts, setIsLoadingLedgerAccounts] = useState(false);
  const [scheduleAmount, setScheduleAmount] = useState<number>(0);
  const [isLoadingScheduleAmount, setIsLoadingScheduleAmount] = useState(false);
  const [userReconciliations, setUserReconciliations] = useState<UserReconciliation[]>([]);
  const [isSavingReconciliation, setIsSavingReconciliation] = useState(false);
  const [isLoadingReconciliation, setIsLoadingReconciliation] = useState(false);
  const [fileUsageReference, setFileUsageReference] = useState<FileUsageEntry[]>([]);

  // Generate question ID for a section
  const getSectionQuestionId = (sectionName: string): string => {
    const normalizedSection = sectionName
      .replace(/[^a-zA-Z0-9\s]/g, "")
      .replace(/\s+/g, "_")
      .toUpperCase();
    return `ADVANCE_FROM_CUSTOMER_COMPLETENESS_${normalizedSection}`;
  };

  const searchForFiles = async () => {
    setFileSearchStatus("searching");
    try {
      if (window.sharePointAPI?.loadComcFiles) {
        const result = await window.sharePointAPI.loadComcFiles("Execution_OTL_AFC");
        if (result.success && result.data?.files) {
          const files = result.data.files
            .map((f: any) => f.name)
            .filter((name: string) => name && name.trim().length > 0);
          if (files.length > 0) {
            setFoundFiles(files);
            setFileSearchStatus("found");
          } else {
            setFileSearchStatus("not_found");
          }
        } else {
          setFileSearchStatus("not_found");
        }
      } else {
        setFileSearchStatus("not_found");
      }
    } catch {
      setFileSearchStatus("not_found");
    }
  };

  const loadFileUsageReference = async () => {
    try {
      const usage = await getProcedureFileUsageForWorkspace("Other Liabilities", "Advance from customer - Completeness Check");
      setFileUsageReference(usage);
    } catch (error) {
      console.error("Error loading file usage reference:", error);
    }
  };

  // Auto-trigger search for files and load ledger accounts when component mounts
  useEffect(() => {
    searchForFiles();
    loadLedgerAccounts();
    loadReconciliationData();
    loadFileUsageReference();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Debug: Log when scheduleAmount changes
  useEffect(() => {
    if (scheduleAmount > 0) {
      console.log(' scheduleAmount state updated to:', scheduleAmount);
    }
  }, [scheduleAmount]);

  // Load ledger accounts: AS → note_line_id 30024, 30049; Ind AS → 30153, 30136
  const loadLedgerAccounts = async () => {
    setIsLoadingLedgerAccounts(true);
    try {
      let targetNoteLineIds: number[] = [30024, 30049];
      try {
        const frfResult = await (window as any).sharePointAPI?.getFrf?.();
        const frf = frfResult?.success ? frfResult.frf : null;
        if (frf === "Indian Accounting Standards (Ind AS)") {
          targetNoteLineIds = [30153, 30136];
        }
      } catch {
        // keep AS default
      }

      if (window.sharePointAPI?.downloadTrialBalanceMapping) {
        const mappingResult = await window.sharePointAPI.downloadTrialBalanceMapping({ includeAmounts: true });
        if (mappingResult?.success && mappingResult?.data) {
          const mappingData = mappingResult.data;
          const accounts: LedgerAccount[] = [];

          for (const [sectionName, ledgers] of Object.entries(mappingData)) {
            if (Array.isArray(ledgers)) {
              ledgers.forEach((item: any) => {
                const noteLineId = item.note_line_id || item.noteLineId;
                
                if (targetNoteLineIds.includes(noteLineId)) {
                  const amountDict = item.amount || {};
                  
                  if (!amountDict || typeof amountDict !== 'object' || Object.keys(amountDict).length === 0) {
                    return;
                  }

                  const parseDate = (dateStr: string): Date => {
                    const [day, month, year] = dateStr.split('/');
                    return new Date(2000 + parseInt(year), parseInt(month) - 1, parseInt(day));
                  };

                  const dates = Object.keys(amountDict);
                  const latestDate = dates.reduce((latest, current) => {
                    try {
                      const latestDateObj = parseDate(latest);
                      const currentDateObj = parseDate(current);
                      return currentDateObj > latestDateObj ? current : latest;
                    } catch {
                      return latest;
                    }
                  }, dates[0]);

                  const latestAmount = parseFloat(amountDict[latestDate]) || 0;
                  let ledgerName = (item.ledger_name || item.ledgerName || '').toString();
                  ledgerName = ledgerName
                    .replace(/_x000D/g, '')
                    .replace(/\n/g, '')
                    .replace(/_+$/, '')
                    .trim();

                  accounts.push({
                    ledger_id: item.ledger_id || item.ledgerId || 0,
                    ledger_name: ledgerName,
                    closing_balance: latestAmount,
                    note_line_id: noteLineId
                  });
                }
              });
            }
          }

          const uniqueAccounts = accounts.reduce((acc, account) => {
            const existing = acc.find(a => a.ledger_id === account.ledger_id);
            if (!existing) {
              acc.push(account);
            } else {
              existing.closing_balance += account.closing_balance;
            }
            return acc;
          }, [] as LedgerAccount[]);

          setAvailableLedgerAccounts(uniqueAccounts);
        }
      }
    } catch (error) {
      console.error('Error loading ledger accounts:', error);
    } finally {
      setIsLoadingLedgerAccounts(false);
    }
  };

  // Load reconciliation data from Execution_OTL_AFC_Response.json
  const loadReconciliationData = async () => {
    setIsLoadingReconciliation(true);
    try {
      if (window.sharePointAPI?.downloadClientJson) {
        const result = await window.sharePointAPI.downloadClientJson('Execution_OTL_AFC_Response.json');
        if (result && typeof result === 'object') {
          const data = (result as any).data || result;
          if (data && typeof data === 'object') {
            setSelectedLedgerAccounts(data.selectedLedgerAccounts || []);
            setUserReconciliations(data.userReconciliations || []);
            // Load scheduleAmount if it exists
            if (data.scheduleAmount !== undefined && data.scheduleAmount !== null) {
              setScheduleAmount(Number(data.scheduleAmount));
            }
          }
        }
      }
    } catch (error) {
      console.log('Reconciliation file not found, will be created on save');
    } finally {
      setIsLoadingReconciliation(false);
    }
  };

  // Save reconciliation data to Execution_OTL_AFC_Response.json
  const saveReconciliationData = async () => {
    // Save column mappings to file usage reference
    const entries: FileUsageEntry[] = [];
    if (selectedFile) {
      const fileName = selectedFile.includes(' (') ? selectedFile.split(' (')[0] : selectedFile;
      entries.push({ "Search for files": fileName });
    }
    const columnMapping: Record<string, string> = {
      partyName: partyNameMapping,
      partyCode: partyCodeMapping,
      amount: amountMapping,
      dateOfReceipt: dateOfReceiptMapping
    };
    if (Object.values(columnMapping).some(v => v)) {
      entries.push({ "Column Mapping": JSON.stringify(columnMapping) });
    }
    if (entries.length > 0) {
      await updateProcedureFileUsageForWorkspace("Other Liabilities", "Advance from customer - Completeness Check", entries);
      await loadFileUsageReference();
    }
    
    setIsSavingReconciliation(true);
    try {
      const data = {
        selectedLedgerAccounts: selectedLedgerAccounts,
        userReconciliations: userReconciliations,
        scheduleAmount: scheduleAmount
      };
      const jsonString = JSON.stringify(data, null, 2);
      const base64Content = btoa(unescape(encodeURIComponent(jsonString)));
      const fyYear = getFY_YEAR();
      if (!fyYear) {
        throw new Error("FY_YEAR is not configured");
      }
      if (window.sharePointAPI?.uploadFile) {
        const result = await window.sharePointAPI.uploadFile({
          fileContent: base64Content,
          fileName: 'Execution_OTL_AFC_Response.json',
          referenceValue: 'Advance from Customer Completeness Check',
          folderName: 'juggernaut',
          fyYear: fyYear,
          sectionList: ['Execution_OTL_AFC_Response']
        });
        if (result.success) {
          alert('Reconciliation data saved successfully!');
        } else {
          alert(`Failed to save: ${result.error || 'Unknown error occurred'}`);
        }
      } else {
        throw new Error('SharePoint API not available');
      }
    } catch (error) {
      console.error('Error saving reconciliation data:', error);
      alert(`Error saving reconciliation data: ${error instanceof Error ? error.message : 'Unknown error occurred'}`);
    } finally {
      setIsSavingReconciliation(false);
    }
  };

  // Calculate total closing balance for selected ledger accounts
  const getTotalLedgerBalance = (): number => {
    return selectedLedgerAccounts.reduce((total, ledgerId) => {
      const account = availableLedgerAccounts.find(acc => acc.ledger_id === ledgerId);
      return total + (account?.closing_balance || 0);
    }, 0);
  };

  // Calculate difference
  const getDifference = (): number => {
    return getTotalLedgerBalance() - scheduleAmount;
  };

  // Calculate net difference
  const getNetDifference = (): number => {
    const difference = getDifference();
    const reconciliationTotal = userReconciliations.reduce((sum, rec) => sum + (rec.amount || 0), 0);
    return difference - reconciliationTotal;
  };

  // Add user reconciliation row
  const addUserReconciliation = () => {
    setUserReconciliations([...userReconciliations, { reason: '', amount: 0 }]);
  };

  // Remove user reconciliation row
  const removeUserReconciliation = (index: number) => {
    setUserReconciliations(userReconciliations.filter((_, i) => i !== index));
  };

  // Update user reconciliation
  const updateUserReconciliation = (index: number, field: 'reason' | 'amount', value: string | number) => {
    setUserReconciliations(userReconciliations.map((rec, i) => 
      i === index ? { ...rec, [field]: value } : rec
    ));
  };

  // Helper to extract JSON from stdout (same pattern as PPE components)
  const extractJSONFromStdout = (stdout: string): any | null => {
    if (!stdout) return null;
    
    const lines = stdout.split(/\r?\n/);
    for (const line of lines) {
      const trimmed = line.trim();
      if (trimmed.startsWith('{') && trimmed.includes('"sum"')) {
        try {
          const parsed = JSON.parse(trimmed);
          if (parsed.sum !== undefined && (parsed.success || parsed.ok)) {
            return parsed;
          }
        } catch (e) {
          // Continue to next line
        }
      }
    }
    return null;
  };

  // Load schedule amount from Excel file - using same pattern as PPE components
  const loadScheduleAmount = async () => {
    if (!selectedFile || !amountMapping) {
      alert('Please select a file and map the Amount column first');
      return;
    }
    setIsLoadingScheduleAmount(true);
    try {
      if (!window.payroll?.run) {
        alert('Payroll API not available');
        setIsLoadingScheduleAmount(false);
        return;
      }

      const result = await window.payroll.run("calculate_column_sum", {
        inputFiles: [],
        options: {
          file_name: selectedFile,
          column_name: amountMapping
        }
      });

      if (!result.ok) {
        alert(`Failed to start calculation: ${result.error || 'Unknown error'}`);
        setIsLoadingScheduleAmount(false);
        return;
      }

      const extracted = extractJSONFromStdout(String(result.stdout || ''));
      if (extracted && extracted.sum !== undefined) {
        setScheduleAmount(Number(extracted.sum));
      }
      setIsLoadingScheduleAmount(false);
    } catch (error) {
      alert(`Error: ${error instanceof Error ? error.message : 'Unknown error'}`);
      setIsLoadingScheduleAmount(false);
    }
  };

  const loadExcelColumns = async () => {
    if (!selectedFile) return;
    setIsLoadingExcelColumns(true);
    try {
      if (window.payroll?.loadExcelColumns) {
        const result = await window.payroll.loadExcelColumns(selectedFile);
        if (result.ok && result.columns) {
          const columns = result.columns.map((col: string) => ({ name: col, value: col }));
          setExcelColumns(columns);
          // Save file name to file usage reference
          const fileName = selectedFile.includes(' (') ? selectedFile.split(' (')[0] : selectedFile;
          await updateProcedureFileUsageForWorkspace("Other Liabilities", "Advance from customer - Completeness Check", [{ 
            "Search for files": fileName 
          }]);
          await loadFileUsageReference();
        }
      } else {
        // Fallback test columns for development
        const testColumns = [
          "Party Name", "Party Code", "Amount", "Date of Receipt",
          "Customer Name", "Customer ID", "Transaction Amount", "Receipt Date"
        ];
        const columns = testColumns.map((col: string) => ({ name: col, value: col }));
        setExcelColumns(columns);
      }
    } catch {
      // Fallback test columns on error
      const testColumns = [
        "Party Name", "Party Code", "Amount", "Date of Receipt",
        "Customer Name", "Customer ID", "Transaction Amount", "Receipt Date"
      ];
      const columns = testColumns.map((col: string) => ({ name: col, value: col }));
      setExcelColumns(columns);
    } finally {
      setIsLoadingExcelColumns(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Advance from customer - Completeness Check</h2>
          <p className="text-gray-400">Map columns and configure completeness check</p>
        </div>
        {onBack && (
          <Button variant="outline" onClick={onBack}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
        )}
      </div>

      <Card className="border-white/10 bg-black/40">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-3 text-white">
              <Search className="h-5 w-5 text-blue-500" />
              Step 1: Search for Existing Files
            </CardTitle>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setReviewCommentQuestionId(getSectionQuestionId("Step 1: Search for Existing Files"))}
              className="flex-shrink-0 p-1 h-7 w-7 text-yellow-400 hover:text-yellow-300 hover:bg-white/10"
              title="Review Comment"
            >
              <MessageSquare className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-3">
            {fileSearchStatus === "searching" && (
              <div className="flex items-center gap-2 text-blue-400">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>Searching for files...</span>
              </div>
            )}
            {fileSearchStatus === "found" && (
              <span className="text-green-400 font-bold"> FOUND!</span>
            )}
            {fileSearchStatus === "not_found" && (
              <span className="text-yellow-400">No files found</span>
            )}
            {fileSearchStatus === "idle" && (
              <span className="text-gray-400">Searching for files...</span>
            )}
          </div>

          {fileSearchStatus === "found" && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-white">Select File</Label>
                <Select value={selectedFile} onValueChange={setSelectedFile}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select file" />
                  </SelectTrigger>
                  <SelectContent className="border-white/10 bg-black/90 text-white">
                    {foundFiles.map((f) => (
                      <SelectItem key={f} value={f}>
                        {f}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {fileUsageReference.find(e => e["Search files"]) && (
                  <p className="text-xs text-white/60 mt-1">
                    Last used: {fileUsageReference.find(e => e["Search files"])?.["Search files"]}
                  </p>
                )}
              </div>
              {selectedFile && (
                <div className="flex items-center gap-3">
                  <Button onClick={loadExcelColumns} disabled={!selectedFile || isLoadingExcelColumns} className="bg-blue-600 hover:bg-blue-700 text-white">
                    {isLoadingExcelColumns ? (
                      <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Loading Columns...</>
                    ) : (
                      "Load Columns"
                    )}
                  </Button>
                  {excelColumns.length > 0 && (
                    <span className="text-green-400 text-sm"> {excelColumns.length} columns loaded</span>
                  )}
                </div>
              )}
            </div>
          )}

          {fileSearchStatus === "not_found" && (
            <div className="space-y-4 p-4 border border-yellow-500/20 bg-yellow-500/5 rounded-lg">
              <div className="text-yellow-400 font-medium">File not found. Please upload a file first.</div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Column Mapping Section - Always visible */}
      <Card className="border-white/10 bg-black/40">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-white">Column Mapping</CardTitle>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setReviewCommentQuestionId(getSectionQuestionId("Column Mapping"))}
              className="flex-shrink-0 p-1 h-7 w-7 text-yellow-400 hover:text-yellow-300 hover:bg-white/10"
              title="Review Comment"
            >
              <MessageSquare className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {excelColumns.length === 0 ? (
            <div className="text-white/60 text-sm">
              Please select a file and click "Load Columns" to populate the dropdowns.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-white">Party Name</Label>
                <Select value={partyNameMapping} onValueChange={setPartyNameMapping}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select column" />
                  </SelectTrigger>
                  <SelectContent className="border-white/10 bg-black/90 text-white">
                    {excelColumns.map((col) => (
                      <SelectItem key={col.value} value={col.value}>
                        {col.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {fileUsageReference.find(e => e["Column Mapping"]) && (() => {
                  try {
                    const mapping = JSON.parse(fileUsageReference.find(e => e["Column Mapping"])?.["Column Mapping"] || "{}");
                    const lastUsed = mapping.partyName;
                    return lastUsed ? (
                      <p className="text-xs text-white/60 mt-1">
                        Last used: {lastUsed}
                      </p>
                    ) : null;
                  } catch {
                    return null;
                  }
                })()}
              </div>

              <div className="space-y-2">
                <Label className="text-white">Party Code</Label>
                <Select value={partyCodeMapping} onValueChange={setPartyCodeMapping}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select column" />
                  </SelectTrigger>
                  <SelectContent className="border-white/10 bg-black/90 text-white">
                    {excelColumns.map((col) => (
                      <SelectItem key={col.value} value={col.value}>
                        {col.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {fileUsageReference.find(e => e["Column Mapping"]) && (() => {
                  try {
                    const mapping = JSON.parse(fileUsageReference.find(e => e["Column Mapping"])?.["Column Mapping"] || "{}");
                    const lastUsed = mapping.partyCode;
                    return lastUsed ? (
                      <p className="text-xs text-white/60 mt-1">
                        Last used: {lastUsed}
                      </p>
                    ) : null;
                  } catch {
                    return null;
                  }
                })()}
              </div>

              <div className="space-y-2">
                <Label className="text-white">Amount</Label>
                <Select value={amountMapping} onValueChange={setAmountMapping}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select column" />
                  </SelectTrigger>
                  <SelectContent className="border-white/10 bg-black/90 text-white">
                    {excelColumns.map((col) => (
                      <SelectItem key={col.value} value={col.value}>
                        {col.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {fileUsageReference.find(e => e["Column Mapping"]) && (() => {
                  try {
                    const mapping = JSON.parse(fileUsageReference.find(e => e["Column Mapping"])?.["Column Mapping"] || "{}");
                    const lastUsed = mapping.amount;
                    return lastUsed ? (
                      <p className="text-xs text-white/60 mt-1">
                        Last used: {lastUsed}
                      </p>
                    ) : null;
                  } catch {
                    return null;
                  }
                })()}
              </div>

              <div className="space-y-2">
                <Label className="text-white">Date of Receipt</Label>
                <Select value={dateOfReceiptMapping} onValueChange={setDateOfReceiptMapping}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select column" />
                  </SelectTrigger>
                  <SelectContent className="border-white/10 bg-black/90 text-white">
                    {excelColumns.map((col) => (
                      <SelectItem key={col.value} value={col.value}>
                        {col.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {fileUsageReference.find(e => e["Column Mapping"]) && (() => {
                  try {
                    const mapping = JSON.parse(fileUsageReference.find(e => e["Column Mapping"])?.["Column Mapping"] || "{}");
                    const lastUsed = mapping.dateOfReceipt;
                    return lastUsed ? (
                      <p className="text-xs text-white/60 mt-1">
                        Last used: {lastUsed}
                      </p>
                    ) : null;
                  } catch {
                    return null;
                  }
                })()}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Completeness Check Section */}
      <Card className="border-white/10 bg-black/40">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-3 text-white">
              <Table className="h-5 w-5" />
              Completeness Check
            </CardTitle>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setReviewCommentQuestionId(getSectionQuestionId("Completeness Check"))}
              className="flex-shrink-0 p-1 h-7 w-7 text-yellow-400 hover:text-yellow-300 hover:bg-white/10"
              title="Review Comment"
            >
              <MessageSquare className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label className="text-white">Ledger accounts to associate</Label>
            <Select
              value=""
              onValueChange={(value) => {
                if (value && !selectedLedgerAccounts.includes(parseInt(value))) {
                  setSelectedLedgerAccounts([...selectedLedgerAccounts, parseInt(value)]);
                }
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder={isLoadingLedgerAccounts ? "Loading..." : "Select ledger accounts"} />
              </SelectTrigger>
              <SelectContent className="border-white/10 bg-black/90 text-white max-h-60">
                {availableLedgerAccounts
                  .filter(acc => !selectedLedgerAccounts.includes(acc.ledger_id))
                  .map((account) => (
                    <SelectItem key={account.ledger_id} value={account.ledger_id.toString()}>
                      {account.ledger_name} (Note Line ID: {account.note_line_id})
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
            {selectedLedgerAccounts.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {selectedLedgerAccounts.map((ledgerId) => {
                  const account = availableLedgerAccounts.find(acc => acc.ledger_id === ledgerId);
                  return account ? (
                    <div key={ledgerId} className="flex items-center gap-1 px-2 py-1 bg-white/10 rounded text-sm text-white">
                      <span>{account.ledger_name}</span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setSelectedLedgerAccounts(selectedLedgerAccounts.filter(id => id !== ledgerId))}
                        className="h-4 w-4 p-0 text-white hover:text-red-400"
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                  ) : null;
                })}
              </div>
            )}
          </div>

          <div className="overflow-x-auto">
            <table className="w-full border-collapse border border-white/10">
              <thead>
                <tr className="bg-black/60">
                  <th className="border border-white/10 px-4 py-2 text-left text-white font-semibold">Particulars</th>
                  <th className="border border-white/10 px-4 py-2 text-right text-white font-semibold">Amount</th>
                </tr>
              </thead>
              <tbody>
                <tr className="hover:bg-white/5">
                  <td className="border border-white/10 px-4 py-2 text-white">As per Sublead (A)</td>
                  <td className="border border-white/10 px-4 py-2 text-right text-white font-semibold">
                    {getTotalLedgerBalance().toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </td>
                </tr>
                <tr className="hover:bg-white/5">
                  <td className="border border-white/10 px-4 py-2 text-white">As per Schedule (B)</td>
                  <td className="border border-white/10 px-4 py-2">
                    <div className="flex items-center justify-end gap-2">
                      <Input
                        type="number"
                        value={scheduleAmount > 0 ? scheduleAmount : ''}
                        onChange={(e) => setScheduleAmount(parseFloat(e.target.value) || 0)}
                        className="border-white/10 bg-black/40 text-white text-right w-40"
                        placeholder="Enter amount"
                      />
                      {amountMapping && selectedFile && (
                        <Button
                          onClick={loadScheduleAmount}
                          disabled={isLoadingScheduleAmount}
                          size="sm"
                          variant="outline"
                          className="border-white/10 text-white"
                        >
                          {isLoadingScheduleAmount ? (
                            <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Calculating...</>
                          ) : (
                            'Auto Calculate'
                          )}
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
                <tr className="hover:bg-white/5">
                  <td className="border border-white/10 px-4 py-2 text-white font-semibold">Difference (C)</td>
                  <td className="border border-white/10 px-4 py-2 text-right text-white font-semibold">
                    {getDifference().toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </td>
                </tr>
                {userReconciliations.map((rec, index) => (
                  <tr key={`reconciliation-${index}`} className="hover:bg-white/5">
                    <td className="border border-white/10 px-4 py-2 text-white">
                      <Input
                        type="text"
                        value={rec.reason}
                        onChange={(e) => updateUserReconciliation(index, 'reason', e.target.value)}
                        className="border-white/10 bg-black/40 text-white"
                        placeholder="Enter reason"
                      />
                    </td>
                    <td className="border border-white/10 px-4 py-2">
                      <div className="flex items-center gap-2">
                        <Input
                          type="number"
                          value={rec.amount || ''}
                          onChange={(e) => updateUserReconciliation(index, 'amount', parseFloat(e.target.value) || 0)}
                          className="border-white/10 bg-black/40 text-white text-right flex-1"
                          placeholder="Enter amount"
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeUserReconciliation(index)}
                          className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
                <tr className="hover:bg-white/5">
                  <td className="border border-white/10 px-4 py-2 text-white font-semibold">Net Difference</td>
                  <td className="border border-white/10 px-4 py-2 text-right text-white font-semibold">
                    {getNetDifference().toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div className="flex justify-between items-center">
            <Button
              onClick={addUserReconciliation}
              size="sm"
              variant="outline"
              className="border-white/10 text-white hover:bg-white/10"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Row
            </Button>
            <Button 
              onClick={saveReconciliationData}
              disabled={isSavingReconciliation}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {isSavingReconciliation ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <FileCheck2 className="h-4 w-4 mr-2" />
                  Save
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Review Comment Modal */}
      {reviewCommentQuestionId && (
        <ReviewComment
          questionId={reviewCommentQuestionId}
          questionText={(() => {
            const sectionMap: Record<string, string> = {
              [getSectionQuestionId("Step 1: Search for Existing Files")]: "Step 1: Search for Existing Files",
              [getSectionQuestionId("Column Mapping")]: "Column Mapping",
              [getSectionQuestionId("Completeness Check")]: "Completeness Check",
            };
            return sectionMap[reviewCommentQuestionId] || reviewCommentQuestionId;
          })()}
          isOpen={!!reviewCommentQuestionId}
          onClose={() => setReviewCommentQuestionId(null)}
        />
      )}
    </div>
  );
}

